import React, { useState, useRef, useEffect } from 'react';

import { IconButton, Badge, Popper, Paper, MenuList, ClickAwayListener, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, TextField, Translate } from '@hydra/common/components';
import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  menuList: {
    padding: theme.spacing(3, 3, 2, 3),
    width: '294px'
  },
  filter: {
    paddingBottom: theme.spacing(1.5),
    display: 'flex'
  },
  clearFilterWrapper: {
    textAlign: 'center',
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5)
  },
  clearFilter: {
    color: theme.palette.error.main,
    fontWeight: theme.typography.fontWeightSemibold,
    cursor: 'pointer'
  },
  label: {
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(0.5)
  },
  middle: {
    paddingBottom: theme.spacing(1)
  },
  textField: {
    maxWidth: '109px'
  },
  divider: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: '22px',
    margin: theme.spacing(1)
  }
}));

type FilterType = 'from' | 'to';
type Filter = { from: string, to: string };
type SetFilter = (filter: Filter) => void;

export default function QueryFilter ({
  clicksFilter,
  setClicksFilter,
  impressionsFilter,
  setImpressionsFilter,
  positionFilter,
  setPositionFilter,
  ctrFilter,
  setCtrFilter
}: {
  clicksFilter: Filter
  setClicksFilter: SetFilter
  impressionsFilter: Filter
  setImpressionsFilter: SetFilter
  positionFilter: Filter
  setPositionFilter: SetFilter
  ctrFilter: Filter
  setCtrFilter: SetFilter
}) {
  const classes = useStyles();
  const { translate } = useI18n();

  const [open, setOpen] = useState<boolean>(false);
  const anchorRef: any = useRef(null);

  const hasFiltersApplied = () => {
    return clicksFilter.from ||
      clicksFilter.to ||
      impressionsFilter.from ||
      impressionsFilter.to ||
      positionFilter.from ||
      positionFilter.to ||
      ctrFilter.from ||
      ctrFilter.to;
  };

  const handleToggle = (event: any) => {
    setOpen(d => !d);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleChangeClicksFilter = (name: FilterType) => (event: any) => {
    setClicksFilter({
      ...clicksFilter,
      [name]: event.target.value
    });
  };

  const handleChangeImpressionsFilter = (name: FilterType) => (event: any) => {
    setImpressionsFilter({
      ...impressionsFilter,
      [name]: event.target.value
    });
  };

  const handleChangePositionFilter = (name: FilterType) => (event: any) => {
    setPositionFilter({
      ...positionFilter,
      [name]: event.target.value
    });
  };

  const handleChangeCtrFilter = (name: FilterType) => (event: any) => {
    setCtrFilter({
      ...ctrFilter,
      [name]: event.target.value
    });
  };

  const handleClearFilter = () => {
    setClicksFilter({ from: '', to: '' });
    setImpressionsFilter({ from: '', to: '' });
    setPositionFilter({ from: '', to: '' });
    setCtrFilter({ from: '', to: '' });
  };

  return (
    <>
      <IconButton ref={anchorRef} size='medium' onClick={handleToggle}>
        <Badge variant='dot' invisible={!hasFiltersApplied()} color='primary'>
          <Icon type='filter' size='md' />
        </Badge>
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-end'
        transition
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList
              disablePadding
              autoFocusItem={open}
              className={classes.menuList}
            >
              <Translate content='gsc_keywords_filter' className={classes.filter} />
              <div className={classes.middle}>
                <Translate content='gsc_keywords_clicks' component='div' className={classes.label} />
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_from')}
                  value={clicksFilter.from}
                  onChange={handleChangeClicksFilter('from')}
                  type='number'
                />
                <span className={classes.divider}>–</span>
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_to')}
                  value={clicksFilter.to}
                  onChange={handleChangeClicksFilter('to')}
                />
              </div>
              <div className={classes.middle}>
                <Translate content='gsc_keywords_impressions' component='div' className={classes.label} />
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_from')}
                  value={impressionsFilter.from}
                  onChange={handleChangeImpressionsFilter('from')}
                  type='number'
                />
                <span className={classes.divider}>–</span>
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_to')}
                  value={impressionsFilter.to}
                  onChange={handleChangeImpressionsFilter('to')}
                  type='number'
                />
              </div>
              <div className={classes.middle}>
                <Translate content='gsc_keywords_position' component='div' className={classes.label} />
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_from')}
                  value={positionFilter.from}
                  onChange={handleChangePositionFilter('from')}
                  type='number'
                />
                <span className={classes.divider}>–</span>
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_to')}
                  value={positionFilter.to}
                  onChange={handleChangePositionFilter('to')}
                  type='number'
                />
              </div>
              <div>
                <Translate content='gsc_keywords_ctr' component='div' className={classes.label} />
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_from')}
                  value={ctrFilter.from}
                  onChange={handleChangeCtrFilter('from')}
                  type='number'
                />
                <span className={classes.divider}>–</span>
                <TextField
                  className={classes.textField}
                  margin='none'
                  disableShadow
                  placeholder={translate('gsc_keywords_filter_to')}
                  value={ctrFilter.to}
                  onChange={handleChangeCtrFilter('to')}
                  type='number'
                />
              </div>
              <div className={classes.clearFilterWrapper}>
                <Link
                  onClick={handleClearFilter}
                  className={classes.clearFilter}
                >
                  <Translate content='gsc_keywords_filter_clear' />
                </Link>
              </div>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </>
  );
}
